import { Component, ElementRef, HostListener, PLATFORM_ID, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService } from '../../../core/services/language.service';
import { ThemeService } from '../../../core/services/theme.service';
import { Router } from '@angular/router';

const MOBILE_BREAKPOINT_PX = 768;

@Component({
  selector: 'app-public-toolbar',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './public-toolbar.component.html',
  styleUrl: './public-toolbar.component.scss'
})
export class PublicToolbarComponent {
  private readonly languageService = inject(LanguageService);
  private readonly themeService = inject(ThemeService);
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  menuOpen = false;
  languageDropdownOpen = false;
  private lastDropdownToggle = 0;

  readonly availableLangs = this.languageService.availableLangs;
  readonly currentLang = this.languageService.currentLang;
  readonly isDark = this.themeService.isDark;

  getCurrentLangOption() {
    return this.languageService.getCurrentLangOption();
  }

  toggleMenu() {
    this.menuOpen = !this.menuOpen;
  }

  toggleLanguageDropdown(event: Event) {
    event.stopPropagation();
    this.languageDropdownOpen = !this.languageDropdownOpen;
    this.lastDropdownToggle = Date.now();
  }

  changeLanguage(lang: string) {
    this.languageService.setLang(lang as 'pt' | 'en' | 'es');
    this.languageDropdownOpen = false;
  }

  goToLoginWithFocus(): void {
    this.menuOpen = false;
    this.languageDropdownOpen = false;

    if (this.router.url.startsWith('/login')) {
      this.requestEmailFocus();
      return;
    }

    this.router.navigate(['/login'], {
      queryParams: { focus: 'email', ts: Date.now() },
    });
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.isBrowser) {
      return;
    }

    if (window.innerWidth > MOBILE_BREAKPOINT_PX && this.menuOpen) {
      this.menuOpen = false;
      this.languageDropdownOpen = false;
    }
  }

  onLanguageSelectChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const selectedLang = target?.value;
    if (!selectedLang) {
      return;
    }

    this.changeLanguage(selectedLang);
  }

  toggleTheme() {
    this.themeService.toggleTheme();
  }

  closeDropdown() {
    this.languageDropdownOpen = false;
  }

  private requestEmailFocus(): void {
    if (!this.isBrowser) {
      return;
    }

    globalThis.window.dispatchEvent(new CustomEvent('focus-login-email'));
  }

  // On iOS Safari and some Android browsers, touch-synthesized click events can bypass
  // stopPropagation() and reach document-level listeners with unreliable event.target.
  // A timestamp guard prevents duplicate events from closing the dropdown immediately.
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (Date.now() - this.lastDropdownToggle < 200) return;

    const target = event.target as Element | null;
    if (!target) return;

    if (this.languageDropdownOpen && !target.closest('.lang-wrapper')) {
      this.languageDropdownOpen = false;
    }

    if (this.menuOpen && !this.elementRef.nativeElement.contains(target)) {
      this.menuOpen = false;
    }
  }
}

